<template>
	<div
		class="ks-video-grid-container"
		:class="{'ks-video-grid-free': free_layout, 'ks-video-grid-animate': animate, 'ks-video-grid-moving': moving}"
		:style="container_style"
	>
		<div
			v-if="moving"
			ref="placeholder"
			class="ks-video-grid-placeholder"
		></div>
		<template v-if="free_layout">
			<div
				v-for="group in display_groups"
				class="ks-video-area-group"
				:class="{'ks-video-area-group-dragging': moving_group === group.id, 'ks-video-area-group-resizing': group_drag && group_drag.id === group.id}"
				:style="group_style(group)"
				:key="'ks-group:' + group.id"
				:data-group="group.id"
			>
				<div class="ks-video-area-group-heading" @pointerdown="group_pointerdown($event, group)">
					<input
						v-if="editing_group === group.id"
						ref="title_input"
						class="ks-video-area-group-title-input"
						v-model="editing_title"
						@keydown.enter.prevent="finish_edit_title"
						@keydown.esc.prevent="cancel_edit_title"
						@blur="finish_edit_title"
					>
					<div
						v-else
						class="ks-video-area-group-title"
						v-tooltip.bottom="'Double-click to rename'"
						@dblclick="start_edit_title(group)"
					>{{group.title || "Group"}}</div>
					<slot name="group-buttons" :group="group" :keys="group_member_keys(group)"></slot>
					<k-btn icon no-margin size="xs" icon-name="fit_screen" v-tooltip.bottom="'Fit to videos'" @click.stop="fit_group(group)"></k-btn>
					<k-label-color-picker right :colors="groupColors" :value="group.color || ''" tooltip="Group color" @input="$emit('group-color', {id: group.id, color: $event})"></k-label-color-picker>
					<k-btn icon no-margin size="xs" icon-name="close" v-tooltip.bottom="'Remove group'" @click.stop="remove_group(group)"></k-btn>
				</div>
				<div
					v-for="dir in group_handles"
					:key="dir"
					:class="'ks-video-area-group-handle ks-video-area-group-handle-' + dir"
					@pointerdown.prevent.stop="start_group_resize($event, group, dir)"
					@pointermove="group_resize_move($event, group)"
					@pointerup="end_group_resize($event)"
					@pointercancel="end_group_resize($event)"
					@lostpointercapture="end_group_resize($event)"
				></div>
			</div>
		</template>
		<div
			v-for="item in display_items"
			class="ks-video-grid-item"
			:class="item_classes(item)"
			:style="item_style(item)"
			:key="item_key(item)"
			:data-key="item_key(item)"
			@pointerdown="item_pointerdown($event, item)"
			@mouseenter.capture="item_mouseenter"
		>
			<slot :item="item"></slot>
			<div
				v-if="can_resize"
				class="ks-video-grid-resize-handle"
				v-tooltip.bottom="'Shift + drag: resize all<br>Double-click: match to previous'"
				@pointerdown.prevent.stop="start_drag($event, item)"
				@pointermove="drag($event, item)"
				@pointerup="end_drag($event)"
				@pointercancel="end_drag($event)"
				@lostpointercapture="end_drag($event)"
				@dblclick.stop="match_previous_size($event, item)"
			></div>
		</div>
	</div>
</template>

<script>
import { TILE_COLORS } from '@/models/monitor.js';
const GAP = 5;
const DEFAULT_WIDTH = 300;
// Videos can be resized down to half of the default width
const MIN_WIDTH = DEFAULT_WIDTH / 2;
const MAX_WIDTH = 1000;
// Items with `free_size: true` can also be resized vertically, down to this height
const MIN_FREE_HEIGHT = 100;
const MAX_FREE_HEIGHT = 2000;
// Resizing snaps to the default width when within this distance of it
const SNAP_DISTANCE = 12;
const MOVE_THRESHOLD = 4;
// Groups: videos inside a group are positioned relative to the group's content area
const GROUP_HEADER = 30;
const GROUP_PADDING = 5;
const MIN_GROUP_WIDTH = 2 * GROUP_PADDING + MIN_WIDTH;
const MIN_GROUP_HEIGHT = 120;
const DEFAULT_GROUP_WIDTH = 2 * GROUP_PADDING + 2 * DEFAULT_WIDTH + GAP;
const DEFAULT_GROUP_HEIGHT = 200;
// Groups can be resized from every edge and corner
const GROUP_HANDLES = ["n", "e", "s", "w", "ne", "nw", "se", "sw"];

function overlaps (a, b) {
	return a.x < b.x + b.w + GAP - 0.5 && b.x < a.x + a.w + GAP - 0.5 &&
		a.y < b.y + b.h + GAP - 0.5 && b.y < a.y + a.h + GAP - 0.5;
}

// Nearest spot for a w*h box that does not overlap any of `placed`.
// Without a target, the topmost (then leftmost) free spot is used.
function find_free (w, h, target, placed, container_width) {
	var max_x = Math.max(0, container_width - w);
	var cx = v => Math.round(Math.min(max_x, Math.max(0, v)));
	var cy = v => Math.round(Math.max(0, v));
	if (target) {
		var t = {x: cx(target.x), y: cy(target.y), w: w, h: h};
		if (!placed.some(r => overlaps(t, r))) return t;
	}
	var xs = [0, max_x];
	var ys = [0];
	var bottom = 0;
	if (target) {
		xs.push(cx(target.x));
		ys.push(cy(target.y));
	}
	placed.forEach(r => {
		xs.push(cx(r.x), cx(r.x + r.w + GAP), cx(r.x - w - GAP), cx(r.x + r.w - w));
		ys.push(cy(r.y), cy(r.y + r.h + GAP), cy(r.y - h - GAP), cy(r.y + r.h - h));
		bottom = Math.max(bottom, r.y + r.h + GAP);
	});
	ys.push(cy(bottom));
	xs = Array.from(new Set(xs));
	ys = Array.from(new Set(ys));
	var free = (x, y) => {
		var c = {x: x, y: y, w: w, h: h};
		return placed.some(r => overlaps(c, r)) ? null : c;
	};
	var i, j, c;
	if (!target) {
		xs.sort((a, b) => a - b);
		ys.sort((a, b) => a - b);
		for (i = 0; i < ys.length; i++) {
			for (j = 0; j < xs.length; j++) {
				if ((c = free(xs[j], ys[i]))) return c;
			}
		}
		return {x: 0, y: cy(bottom), w: w, h: h};
	}
	// Candidates sorted by distance per axis so the search can stop once nothing closer is possible
	var tx = target.x;
	var ty = target.y;
	xs.sort((a, b) => Math.abs(a - tx) - Math.abs(b - tx));
	ys.sort((a, b) => Math.abs(a - ty) - Math.abs(b - ty));
	var best = null;
	var best_cost = Infinity;
	for (i = 0; i < ys.length; i++) {
		var dy2 = (ys[i] - ty) * (ys[i] - ty);
		if (dy2 >= best_cost) break;
		for (j = 0; j < xs.length; j++) {
			var cost = dy2 + (xs[j] - tx) * (xs[j] - tx);
			if (cost >= best_cost) break;
			if ((c = free(xs[j], ys[i]))) {
				best = c;
				best_cost = cost;
				break;
			}
		}
	}
	return best || {x: cx(tx), y: cy(bottom), w: w, h: h};
}

export default {
	props: {
		// Items are keyed by id (or uuid); `free_size: true` makes the height resizable too
		// (the item content should then fill the given height)
		"items": {
			type: Array,
			default: function() {return []}
		},
		"disable-resizing": {
			type: Boolean,
			default: false
		},
		// Disables moving the items (they are shown in a wrapping row layout instead)
		"disable-reordering": {
			type: Boolean,
			default: false
		},
		// Saved layout given by the parent ({sizes, positions, group_rects, members, free_heights}). Every change
		// is reported with "layout-change" (layout) for the parent to save; KVideoArea doesn't store anything
		// itself. Changes of this prop from elsewhere (e.g. another browser) are applied.
		"saved-layout": {
			type: Object,
			default: null
		},
		// Max number of sizes/positions entries in the reported layout (0 = no limit)
		"max-entries": {
			type: Number,
			default: 0
		},
		// [{id, title, color, items}]; items are keys moved into the group when it first appears.
		// Group geometry and membership are saved with the layout; the list itself is owned by
		// the parent, which handles the "group-title" ({id, title}), "group-color" ({id, color})
		// and "group-remove" (id, item keys) events. "group-removed" (id, item keys) tells which shown videos
		// were inside a group when it disappeared from the groups prop.
		// Slot "group-buttons" ({group, keys}) adds buttons to the group heading; keys are the shown items inside it
		"groups": {
			type: Array,
			default: function() {return []}
		},
		// Colors for the group color picker, [{value, title, color}]; group.color is one of the values
		"group-colors": {
			type: Array,
			default: function() {return TILE_COLORS}
		}
	},
	data () {
		var saved = this.load_layout();
		return {
			sizes: saved.sizes,
			// User set heights of free size items
			free_heights: saved.free_heights,
			drag_state: null,
			// Positions of group members are relative to the group's content area
			positions: saved.positions,
			// {group id: {x, y, w, h}}; h is the user set height, the group grows to fit its videos
			group_rects: saved.group_rects,
			// {item key: group id}
			members: saved.members,
			group_drag: null,
			group_handles: GROUP_HANDLES,
			moving_group: null,
			editing_group: null,
			editing_title: "",
			legacy_order: saved.order,
			heights: {},
			container_width: 0,
			// Key of the item being moved; the pointer state itself is kept non-reactive in this.move
			moving_key: null,
			// Container height needed by the landing spot; the spot itself is kept in this.move
			landing_bottom: 0,
			animate: false,
			// Matches the max-width: 768px media rule where items are full width
			narrow: false
		}
	},
	watch: {
		savedLayout (value) {
			this.apply_external_layout(value);
		},
		groups: {
			immediate: true,
			handler (groups, old) {
				var ids = {};
				groups.forEach(g => { ids[g.id] = true; });
				var changed = false;
				var shown = {};
				this.items.forEach(i => { shown[this.item_key(i)] = true; });
				// Videos of a removed group stay where they were shown; the parent gets the ones still shown
				// with "group-removed" (id, item keys) and decides whether to keep them
				(old || []).forEach(g => {
					if (ids[g.id]) return;
					var gr = this.group_rects[g.id] || {x: 0, y: 0};
					var keys = [];
					Object.keys(this.members).forEach(key => {
						if (this.members[key] !== g.id) return;
						var p = this.positions[key] || {x: 0, y: 0};
						this.$set(this.positions, key, {x: gr.x + GROUP_PADDING + p.x, y: gr.y + GROUP_HEADER + p.y});
						this.$delete(this.members, key);
						if (shown[key]) keys.push(key);
					});
					this.$delete(this.group_rects, g.id);
					changed = true;
					if (keys.length) this.$emit("group-removed", g.id, keys);
				});
				// New groups go to the first free spot from the top left that fits them;
				// videos listed in group.items are moved inside
				var added = groups.filter(g => !this.group_rects[g.id]);
				if (added.length) {
					var adopted = {};
					added.forEach(g => (Array.isArray(g.items) ? g.items : []).forEach(k => { adopted[k] = true; }));
					// full_layout skips groups without a rect, so this is the layout without the new groups
					var fl = this.full_layout;
					var placed = Object.keys(fl.groups).map(id => fl.groups[id])
						.concat(Object.keys(fl.items).filter(k => !adopted[k] && !fl.local[k]).map(k => fl.items[k]));
					var cw = this.container_width;
					added.forEach(g => {
						var keys = Array.isArray(g.items) ? g.items : [];
						var n = Math.max(2, keys.length);
						var w = 2 * GROUP_PADDING + n * DEFAULT_WIDTH + (n - 1) * GAP;
						if (cw) w = Math.max(MIN_GROUP_WIDTH, Math.min(w, cw));
						var r = find_free(w, this.estimate_group_height(w, keys), null, placed, cw);
						placed.push(r);
						this.$set(this.group_rects, g.id, {x: r.x, y: r.y, w: w, h: DEFAULT_GROUP_HEIGHT});
						keys.forEach(k => {
							this.$set(this.members, k, g.id);
							this.$delete(this.positions, k);
						});
					});
					changed = true;
				}
				if (changed && old) {
					this.$nextTick(() => {
						this.commit_layout();
						this.save_layout();
					});
				}
			}
		}
	},
	created () {
		this.move = null;
		this.move_frame = null;
		this.resize_frame = null;
		this.resize_event = null;
	},
	mounted () {
		this.container_width = this.$el.clientWidth;
		this.observed = new Set();
		if (window.ResizeObserver) {
			this.resize_observer = new ResizeObserver(entries => this.on_resize(entries));
			this.resize_observer.observe(this.$el);
		}
		this.observe_items();
		if (window.matchMedia) {
			this.narrow_query = window.matchMedia("(max-width: 768px)");
			this.narrow = this.narrow_query.matches;
			this.on_narrow = e => { this.narrow = e.matches; };
			if (this.narrow_query.addEventListener) this.narrow_query.addEventListener("change", this.on_narrow);
			else this.narrow_query.addListener(this.on_narrow);
		}
	},
	updated () {
		this.observe_items();
	},
	beforeDestroy () {
		if (this.resize_observer) this.resize_observer.disconnect();
		if (this.narrow_query) {
			if (this.narrow_query.removeEventListener) this.narrow_query.removeEventListener("change", this.on_narrow);
			else this.narrow_query.removeListener(this.on_narrow);
		}
		this.stop_move();
		cancelAnimationFrame(this.resize_frame);
		if (this.group_drag && this.group_drag.frame !== null) cancelAnimationFrame(this.group_drag.frame);
		clearTimeout(this.animate_timer);
	},
	computed: {
		can_resize () {
			return !this.disableResizing && !this.is_compact();
		},
		can_reorder () {
			return !this.disableReordering && !this.is_compact();
		},
		free_layout () {
			return this.can_reorder;
		},
		moving () {
			return this.moving_key !== null || this.moving_group !== null;
		},
		display_groups () {
			return this.groups.filter(g => this.group_layout[g.id]);
		},
		// DOM order stays the same in the free layout so videos are not re-attached when moved
		display_items () {
			if (this.free_layout) return this.items;
			var items = this.items.slice();
			var pos = this.positions;
			var keyed = items.map((item, idx) => ({item: item, idx: idx, p: pos[this.item_key(item)]}));
			keyed.sort((a, b) => {
				if (a.p && b.p) return (a.p.y - b.p.y) || (a.p.x - b.p.x) || (a.idx - b.idx);
				if (a.p || b.p) return a.p ? -1 : 1;
				return a.idx - b.idx;
			});
			return keyed.map(k => k.item);
		},
		// Resolved non-overlapping rectangles {x, y, w, h}: items and groups in container coordinates,
		// local = group members relative to their group's content area.
		// Groups are placed first, then their members inside them, then the other items around the groups.
		full_layout () {
			var result = {items: {}, groups: {}, local: {}};
			var cw = this.container_width;
			if (!this.free_layout || !cw) return result;
			var active = this.drag_state && this.drag_state.key;
			var active_group = this.group_drag && this.group_drag.id;
			var legacy = {};
			this.legacy_order.forEach((k, i) => { legacy[k] = i; });
			// Groups get a rect from the groups watcher; until then they are not laid out
			var groups = this.groups.filter(g => this.group_rects[g.id]);
			var group_w = {};
			groups.forEach(g => {
				var gr = this.group_rects[g.id] || {};
				group_w[g.id] = Math.min(Math.max(MIN_GROUP_WIDTH, gr.w || DEFAULT_GROUP_WIDTH), cw);
			});
			var entries = this.items.map((item, idx) => {
				var key = this.item_key(item);
				if (legacy[key] !== undefined) idx = legacy[key] - this.legacy_order.length;
				var group = this.members[key];
				if (group_w[group] === undefined) group = null;
				var w = this.narrow ? cw : Math.min((this.can_resize && this.sizes[key]) || DEFAULT_WIDTH, cw);
				if (group) w = Math.max(1, Math.min(w, group_w[group] - 2 * GROUP_PADDING));
				var h = (item.free_size && this.free_heights[key]) || this.heights[key] || Math.round(w * 9 / 16);
				return {key: key, idx: idx, w: w, h: h, pos: this.positions[key] || null, group: group};
			});
			// The item (or group) being resized keeps its place; others are placed in reading order around it
			var order = (active_key) => (a, b) => {
				if (a.key === active_key || b.key === active_key) return a.key === active_key ? -1 : 1;
				if (a.pos && b.pos) return (a.pos.y - b.pos.y) || (a.pos.x - b.pos.x) || (a.idx - b.idx);
				if (a.pos || b.pos) return a.pos ? -1 : 1;
				return a.idx - b.idx;
			};
			var group_entries = groups.map((g, idx) => {
				var gr = this.group_rects[g.id] || {};
				var w = group_w[g.id];
				var inner = Math.max(1, w - 2 * GROUP_PADDING);
				var placed = [];
				var bottom = 0;
				entries.filter(e => e.group === g.id).sort(order(active)).forEach(e => {
					var r = find_free(e.w, e.h, e.pos, placed, inner);
					result.local[e.key] = r;
					placed.push(r);
					bottom = Math.max(bottom, r.y + r.h);
				});
				var h = Math.max(MIN_GROUP_HEIGHT, gr.h || DEFAULT_GROUP_HEIGHT, GROUP_HEADER + bottom + GROUP_PADDING);
				var pos = isFinite(gr.x) && isFinite(gr.y) ? {x: gr.x, y: gr.y} : null;
				return {key: g.id, idx: idx, w: w, h: h, pos: pos};
			});
			var placed = [];
			group_entries.sort(order(active_group)).forEach(e => {
				var r = find_free(e.w, e.h, e.pos, placed, cw);
				result.groups[e.key] = r;
				placed.push(r);
			});
			entries.forEach(e => {
				if (!e.group) return;
				var g = result.groups[e.group];
				var l = result.local[e.key];
				result.items[e.key] = {x: g.x + GROUP_PADDING + l.x, y: g.y + GROUP_HEADER + l.y, w: l.w, h: l.h};
			});
			entries.filter(e => !e.group).sort(order(active)).forEach(e => {
				var r = find_free(e.w, e.h, e.pos, placed, cw);
				result.items[e.key] = r;
				placed.push(r);
			});
			return result;
		},
		layout () {
			return this.full_layout.items;
		},
		group_layout () {
			return this.full_layout.groups;
		},
		container_style () {
			if (!this.free_layout) return null;
			var bottom = this.layout_bottom();
			if (this.moving) bottom = Math.max(bottom, this.landing_bottom);
			return {height: bottom + "px"};
		}
	},
	methods: {
		clamp_width (v) {
			v = Number(v);
			if (!isFinite(v)) return null;
			return Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, v)));
		},
		// Called from data(), so it must not touch reactive state
		load_layout () {
			// The loaded layout counts as our own, so it isn't applied again when the parent's data is updated
			// while a newer change is waiting to be saved
			this._sent = this.savedLayout ? [_.cloneDeep(this.savedLayout)] : [];
			return this.parse_layout(this.savedLayout);
		},
		// Checks and cleans a saved layout
		parse_layout (data) {
			var result = {sizes: {}, free_heights: {}, positions: {}, order: [], group_rects: {}, members: {}};
			if (!data || typeof data !== "object") return result;
			try {
				if (data.sizes && typeof data.sizes === "object") {
					Object.keys(data.sizes).forEach(k => {
						var w = this.clamp_width(data.sizes[k]);
						if (w !== null) result.sizes[k] = w;
					});
				}
				if (data.positions && typeof data.positions === "object") {
					Object.keys(data.positions).forEach(k => {
						var p = data.positions[k];
						if (!p || !isFinite(p.x) || !isFinite(p.y)) return;
						result.positions[k] = {x: Math.max(0, Math.round(p.x)), y: Math.max(0, Math.round(p.y))};
					});
				}
				// Order saved by the older row layout: used for the initial placement of unpositioned items
				if (Array.isArray(data.order)) {
					result.order = data.order.filter(k => typeof k === "string" || typeof k === "number").map(String);
				}
				if (data.group_rects && typeof data.group_rects === "object") {
					Object.keys(data.group_rects).forEach(id => {
						var g = data.group_rects[id];
						if (!g || !isFinite(g.x) || !isFinite(g.y)) return;
						result.group_rects[id] = {
							x: Math.max(0, Math.round(g.x)),
							y: Math.max(0, Math.round(g.y)),
							w: isFinite(g.w) ? Math.max(MIN_GROUP_WIDTH, Math.round(g.w)) : DEFAULT_GROUP_WIDTH,
							h: isFinite(g.h) ? Math.max(MIN_GROUP_HEIGHT, Math.round(g.h)) : DEFAULT_GROUP_HEIGHT
						};
					});
				}
				if (data.free_heights && typeof data.free_heights === "object") {
					Object.keys(data.free_heights).forEach(k => {
						var fh = Number(data.free_heights[k]);
						if (isFinite(fh)) result.free_heights[k] = Math.round(Math.min(MAX_FREE_HEIGHT, Math.max(MIN_FREE_HEIGHT, fh)));
					});
				}
				if (data.members && typeof data.members === "object") {
					Object.keys(data.members).forEach(k => {
						if (typeof data.members[k] === "string") result.members[k] = data.members[k];
					});
				}
			} catch (err) {
				// Corrupt data: start with defaults
			}
			return result;
		},
		// The layout to save; keys beyond max-entries are dropped
		serialize_layout () {
			var keys = Object.keys(this.sizes);
			var pos_keys = Object.keys(this.positions);
			// Optionally keep only the most recently added entries so stale keys (e.g. old events) cannot grow forever
			if (this.maxEntries > 0) {
				keys = keys.slice(-this.maxEntries);
				pos_keys = pos_keys.slice(-this.maxEntries);
			}
			var sizes = {};
			keys.forEach(k => { sizes[k] = this.sizes[k]; });
			var positions = {};
			pos_keys.forEach(k => { positions[k] = this.positions[k]; });
			var members = {};
			pos_keys.forEach(k => { if (this.members[k]) members[k] = this.members[k]; });
			var free_heights = {};
			keys.forEach(k => { if (this.free_heights[k]) free_heights[k] = this.free_heights[k]; });
			return _.cloneDeep({
				sizes: sizes,
				positions: positions,
				group_rects: this.group_rects,
				members: members,
				free_heights: free_heights
			});
		},
		// Called when a resize or move action has finished
		save_layout () {
			if (!this.can_resize && !this.can_reorder) return;
			var layout = this.serialize_layout();
			// The parent's saves come back through the saved-layout prop, possibly after newer changes;
			// these are recognized and not applied again
			this._sent = (this._sent || []).concat([layout]).slice(-30);
			this.$emit("layout-change", layout);
		},
		// A changed saved-layout prop that isn't one of our own saves (e.g. saved in another browser)
		apply_external_layout (layout) {
			if (!layout || this.drag_state || this.group_drag || this.moving) return;
			if ((this._sent || []).some(sent => _.isEqual(sent, layout))) return;
			if (_.isEqual(this.serialize_layout(), layout)) return;
			var parsed = this.parse_layout(layout);
			this.sizes = parsed.sizes;
			this.free_heights = parsed.free_heights;
			this.positions = parsed.positions;
			this.group_rects = parsed.group_rects;
			this.members = parsed.members;
		},
		// Stores the currently resolved positions so items and groups stay where they are shown
		commit_layout () {
			var fl = this.full_layout;
			Object.keys(fl.items).forEach(k => {
				var r = fl.local[k] || fl.items[k];
				var p = this.positions[k];
				if (!p || p.x !== r.x || p.y !== r.y) this.$set(this.positions, k, {x: r.x, y: r.y});
			});
			Object.keys(fl.groups).forEach(id => {
				var r = fl.groups[id];
				var g = this.group_rects[id] || {};
				if (g.x !== r.x || g.y !== r.y) {
					this.$set(this.group_rects, id, {x: r.x, y: r.y, w: g.w || DEFAULT_GROUP_WIDTH, h: g.h || DEFAULT_GROUP_HEIGHT});
				}
			});
		},
		// Height a new group needs for the videos `keys`, laid out like full_layout does.
		// Unmeasured videos use the heading/footer height of a measured one on top of the 16:9 video.
		estimate_group_height (w, keys) {
			var inner = Math.max(1, w - 2 * GROUP_PADDING);
			var extra = 0;
			var layout = this.layout;
			var measured = Object.keys(layout).find(k => this.heights[k]);
			if (measured) extra = Math.max(0, this.heights[measured] - Math.round(layout[measured].w * 9 / 16));
			var placed = [];
			var bottom = 0;
			keys.forEach(k => {
				var iw = Math.min((this.can_resize && this.sizes[k]) || DEFAULT_WIDTH, inner);
				var r = find_free(iw, this.heights[k] || Math.round(iw * 9 / 16) + extra, null, placed, inner);
				placed.push(r);
				bottom = Math.max(bottom, r.y + r.h);
			});
			return Math.max(DEFAULT_GROUP_HEIGHT, GROUP_HEADER + bottom + GROUP_PADDING);
		},
		// Bottom edge of the laid out items and groups
		layout_bottom () {
			var fl = this.full_layout;
			var bottom = 0;
			Object.keys(fl.items).forEach(k => { bottom = Math.max(bottom, fl.items[k].y + fl.items[k].h); });
			Object.keys(fl.groups).forEach(id => {
				bottom = Math.max(bottom, fl.groups[id].y + fl.groups[id].h);
			});
			return bottom;
		},
		group_style (group) {
			var r = this.group_layout[group.id];
			if (!r) return null;
			var style = this.rect_style(r);
			var c = group.color && this.groupColors.find(c => c.value === group.color);
			if (c && c.color) style["--ks-video-area-group-background"] = c.color;
			return style;
		},
		start_animation () {
			clearTimeout(this.animate_timer);
			this.animate = true;
		},
		stop_animation () {
			clearTimeout(this.animate_timer);
			this.animate_timer = setTimeout(() => { this.animate = false; }, 300);
		},
		observe_items () {
			if (!this.resize_observer) return;
			Array.from(this.$el.children).forEach(el => {
				if (el.dataset.key === undefined || this.observed.has(el)) return;
				this.observed.add(el);
				this.resize_observer.observe(el);
			});
			this.observed.forEach(el => {
				if (el.isConnected && el.parentNode === this.$el) return;
				this.observed.delete(el);
				this.resize_observer.unobserve(el);
			});
		},
		on_resize (entries) {
			entries.forEach(entry => {
				var el = entry.target;
				if (el === this.$el) {
					if (this.container_width !== el.clientWidth) this.container_width = el.clientWidth;
					return;
				}
				var key = el.dataset.key;
				if (key === undefined) return;
				var h = el.offsetHeight;
				if (h && this.heights[key] !== h) this.$set(this.heights, key, h);
			});
		},
		rect_style (r) {
			return {transform: "translate(" + r.x + "px, " + r.y + "px)", width: r.w + "px", height: r.h + "px"};
		},
		item_classes (item) {
			if (!this.can_reorder) return null;
			return {
				"ks-video-grid-item-reorderable": true,
				"ks-video-grid-item-dragging": this.moving_key !== null && this.moving_key === this.item_key(item)
			};
		},
		// Moving can only be started from the panel heading (not from its buttons)
		close_tooltips (root) {
			if (!root || !root.querySelectorAll) return;
			root.querySelectorAll("*").forEach(el => {
				if (el.__vueTooltipLeave__) el.__vueTooltipLeave__();
			});
		},
		// Capture-phase handler: stops tooltip directives inside the heading from seeing mouseenter
		item_mouseenter (e) {
			if (!this.can_reorder) return;
			var t = e.target;
			if (t && t.closest && t.closest(".ks-panel-heading")) e.stopPropagation();
		},
		item_pointerdown (e, item) {
			if (!this.can_reorder || e.button !== 0 || this.drag_state || this.group_drag) return;
			var t = e.target;
			if (!t.closest || !t.closest(".ks-panel-heading")) return;
			if (t.closest("button, a, input, select, textarea, [role=button], .ks-btn")) return;
			var key = this.item_key(item);
			var r = this.layout[key];
			if (!r) return;
			this.begin_move(e, "item", key, e.currentTarget, r);
		},
		group_pointerdown (e, group) {
			if (!this.can_reorder || e.button !== 0 || this.drag_state || this.group_drag) return;
			var t = e.target;
			if (t.closest("button, a, input, select, textarea, [role=button], .ks-btn")) return;
			var r = this.group_layout[group.id];
			if (!r) return;
			this.begin_move(e, "group", group.id, e.currentTarget.parentElement, r);
		},
		// kind "item" moves one video (into, out of or between groups), "group" moves a group with its videos
		begin_move (e, kind, key, el, r) {
			// A previous move whose pointerup was lost must not block this one
			if (this.move) this.stop_move();
			var c = this.$el.getBoundingClientRect();
			this.move = {
				kind: kind,
				key: key,
				el: el,
				pointer_id: e.pointerId,
				start_x: e.clientX,
				start_y: e.clientY,
				client_x: e.clientX,
				client_y: e.clientY,
				// Pointer offset inside the item, so page scrolling during the move is handled
				offset_x: e.clientX - c.left - r.x,
				offset_y: e.clientY - c.top - r.y,
				x: r.x,
				y: r.y,
				w: r.w,
				h: r.h,
				moved: false,
				placed: null,
				placed_bottom: 0,
				// Item moves: group rects and the other members' local rects per group
				groups: null,
				group_placed: null,
				target_el: null,
				// Group moves: the group's videos {el, key, dx, dy} that follow it
				followers: null,
				landing: null,
				placeholder: null,
				scroller: null
			};
			window.addEventListener("pointermove", this.on_move);
			window.addEventListener("pointerup", this.on_move_end);
			window.addEventListener("pointercancel", this.on_move_cancel);
			// pointerdown is not prevented (popups close on mousedown), so a native drag or
			// text selection could start and cancel the pointer stream
			window.addEventListener("dragstart", this.prevent_default, true);
			window.addEventListener("selectstart", this.prevent_default, true);
		},
		// Rectangles the moved item or group must not overlap, computed once when the move starts
		prepare_move (s) {
			var fl = this.full_layout;
			var key = String(s.key);
			var group_keys = Object.keys(fl.groups);
			if (s.kind === "group") {
				s.placed = group_keys.filter(id => id !== key).map(id => fl.groups[id])
					.concat(Object.keys(fl.items).filter(k => this.members[k] !== key).map(k => fl.items[k]));
				s.followers = [];
				Array.from(this.$el.children).forEach(el => {
					var k = el.dataset.key;
					if (k === undefined || this.members[k] !== key || !fl.items[k] || !fl.local[k]) return;
					s.followers.push({el: el, key: k, dx: fl.items[k].x - s.x, dy: fl.items[k].y - s.y});
				});
				this.moving_group = s.key;
			} else {
				s.placed = Object.keys(fl.items).filter(k => k !== key).map(k => fl.items[k])
					.concat(group_keys.map(id => fl.groups[id]));
				s.groups = fl.groups;
				s.group_placed = {};
				group_keys.forEach(id => {
					s.group_placed[id] = Object.keys(fl.local).filter(k => k !== key && this.members[k] === id).map(k => fl.local[k]);
				});
				this.moving_key = s.key;
			}
			s.placed_bottom = s.placed.reduce((b, r) => Math.max(b, r.y + r.h), 0);
		},
		// Group under the pointer (in container coordinates) for an item move
		target_group (s, px, py) {
			var ids = Object.keys(s.groups);
			for (var i = 0; i < ids.length; i++) {
				var g = s.groups[ids[i]];
				if (px >= g.x && px <= g.x + g.w && py >= g.y && py <= g.y + g.h) return ids[i];
			}
			return null;
		},
		set_drop_target (s, id) {
			var el = id ? Array.from(this.$el.children).find(c => c.dataset.group === id) || null : null;
			if (el === s.target_el) return;
			if (s.target_el) s.target_el.classList.remove("ks-video-area-group-drop");
			if (el) el.classList.add("ks-video-area-group-drop");
			s.target_el = el;
		},
		prevent_default (e) {
			e.preventDefault();
		},
		find_scroller () {
			for (var el = this.$el.parentElement; el && el !== document.body; el = el.parentElement) {
				var oy = window.getComputedStyle(el).overflowY;
				if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) return el;
			}
			return document.scrollingElement || document.documentElement;
		},
		// Scroll speed (px per frame) when the pointer is near the top or bottom edge of the scroll area
		autoscroll_speed (s) {
			var el = s.scroller;
			if (!el) return 0;
			var top = 0;
			var bottom = window.innerHeight;
			if (el !== document.scrollingElement && el !== document.documentElement) {
				var r = el.getBoundingClientRect();
				top = Math.max(top, r.top);
				bottom = Math.min(bottom, r.bottom);
			}
			var zone = 50;
			var max = 20;
			if (s.client_y < top + zone && el.scrollTop > 0) {
				return -Math.ceil(max * Math.min(1, (top + zone - s.client_y) / zone));
			}
			if (s.client_y > bottom - zone && el.scrollTop + el.clientHeight < el.scrollHeight) {
				return Math.ceil(max * Math.min(1, (s.client_y - bottom + zone) / zone));
			}
			return 0;
		},
		schedule_move () {
			if (this.move_frame === null) this.move_frame = requestAnimationFrame(this.apply_move);
		},
		on_move_scroll () {
			if (this.move && this.move.moved) this.schedule_move();
		},
		on_lost_capture (e) {
			var s = this.move;
			if (!s || e.pointerId !== s.pointer_id) return;
			// Capture lost without pointerup (e.g. window lost focus): drop where it is shown
			this.on_move_end(e);
		},
		on_move (e) {
			var s = this.move;
			if (!s || e.pointerId !== s.pointer_id) return;
			if (e.buttons === 0) {
				this.on_move_end(e);
				return;
			}
			s.client_x = e.clientX;
			s.client_y = e.clientY;
			if (!s.moved) {
				if (Math.hypot(e.clientX - s.start_x, e.clientY - s.start_y) < MOVE_THRESHOLD) return;
				s.moved = true;
				this.close_tooltips(s.el);
				// The other items don't move while dragging, so their rectangles are computed once
				this.prepare_move(s);
				if (s.kind === "group") {
					// The group's videos follow the pointer directly; a transition would make them lag
					clearTimeout(this.animate_timer);
					this.animate = false;
				} else {
					this.start_animation();
				}
				// Captured pointer events keep coming even when the pointer is over videos or outside the window.
				// Captured only now so that a plain click still reaches the heading.
				try {
					s.el.setPointerCapture(s.pointer_id);
					s.el.addEventListener("lostpointercapture", this.on_lost_capture);
				} catch (err) {
					// Pointer already released
				}
				s.scroller = this.find_scroller();
				window.addEventListener("scroll", this.on_move_scroll, true);
			}
			e.preventDefault();
			// Pointer events can fire many times per frame: update at most once per frame
			this.schedule_move();
		},
		// `final` is true when called on drop; from requestAnimationFrame it gets a timestamp
		apply_move (final) {
			if (this.move_frame !== null) {
				cancelAnimationFrame(this.move_frame);
				this.move_frame = null;
			}
			var s = this.move;
			if (!s || !s.moved) return;
			var speed = final === true ? 0 : this.autoscroll_speed(s);
			if (speed) {
				s.scroller.scrollTop += speed;
				// Keep scrolling while the pointer stays in the edge zone
				this.schedule_move();
			}
			var c = this.$el.getBoundingClientRect();
			s.x = Math.round(Math.min(Math.max(0, this.container_width - s.w), Math.max(0, s.client_x - c.left - s.offset_x)));
			s.y = Math.round(Math.max(0, s.client_y - c.top - s.offset_y));
			// Moved directly (not through a re-render), the compositor handles the transform
			s.el.style.transform = "translate(" + s.x + "px, " + s.y + "px)";
			(s.followers || []).forEach(f => {
				f.el.style.transform = "translate(" + (s.x + f.dx) + "px, " + (s.y + f.dy) + "px)";
			});
			var target = null;
			if (s.kind === "item") {
				target = this.target_group(s, s.client_x - c.left, s.client_y - c.top);
				this.set_drop_target(s, target);
			}
			var l;
			if (target) {
				var g = s.groups[target];
				var inner = Math.max(1, g.w - 2 * GROUP_PADDING);
				var local = find_free(Math.min(s.w, inner), s.h, {x: s.x - g.x - GROUP_PADDING, y: s.y - g.y - GROUP_HEADER}, s.group_placed[target], inner);
				l = {x: g.x + GROUP_PADDING + local.x, y: g.y + GROUP_HEADER + local.y, w: local.w, h: local.h, group: target, local: local};
			} else {
				l = find_free(s.w, s.h, {x: s.x, y: s.y}, s.placed, this.container_width);
			}
			var old = s.landing;
			var p = this.$refs.placeholder;
			// Placeholder is updated directly: a reactive change would re-render every item each frame
			if (!old || old.x !== l.x || old.y !== l.y || old.group !== l.group || p !== s.placeholder) {
				s.landing = l;
				s.placeholder = p;
				if (p) Object.assign(p.style, this.rect_style(l));
			}
			var bottom = Math.max(s.placed_bottom, l.y + l.h);
			if (bottom !== this.landing_bottom) this.landing_bottom = bottom;
		},
		on_move_end (e) {
			var s = this.move;
			if (!s || (e && e.pointerId !== s.pointer_id)) return;
			if (s.moved) {
				this.apply_move(true);
				var l = s.landing;
				if (l) {
					this.commit_layout();
					if (s.kind === "group") {
						var g = this.group_rects[s.key] || {};
						this.$set(this.group_rects, s.key, {x: l.x, y: l.y, w: g.w || DEFAULT_GROUP_WIDTH, h: g.h || DEFAULT_GROUP_HEIGHT});
					} else if (l.group) {
						this.$set(this.members, s.key, l.group);
						this.$set(this.positions, s.key, {x: l.local.x, y: l.local.y});
					} else {
						this.$delete(this.members, s.key);
						this.$set(this.positions, s.key, {x: l.x, y: l.y});
					}
					this.save_layout();
				}
				// Don't let the end of the move act as a click on the heading
				var swallow = ev => { ev.stopPropagation(); ev.preventDefault(); };
				window.addEventListener("click", swallow, {capture: true, once: true});
				setTimeout(() => window.removeEventListener("click", swallow, {capture: true}), 0);
			}
			this.stop_move();
		},
		on_move_cancel (e) {
			var s = this.move;
			if (!s || e.pointerId !== s.pointer_id) return;
			this.stop_move();
		},
		stop_move () {
			window.removeEventListener("pointermove", this.on_move);
			window.removeEventListener("pointerup", this.on_move_end);
			window.removeEventListener("pointercancel", this.on_move_cancel);
			window.removeEventListener("dragstart", this.prevent_default, true);
			window.removeEventListener("selectstart", this.prevent_default, true);
			window.removeEventListener("scroll", this.on_move_scroll, true);
			if (this.move_frame !== null) {
				cancelAnimationFrame(this.move_frame);
				this.move_frame = null;
			}
			var s = this.move;
			this.move = null;
			if (!s || !s.moved) return;
			s.el.removeEventListener("lostpointercapture", this.on_lost_capture);
			if (s.el.hasPointerCapture && s.el.hasPointerCapture(s.pointer_id)) {
				s.el.releasePointerCapture(s.pointer_id);
			}
			if (s.target_el) s.target_el.classList.remove("ks-video-area-group-drop");
			// Restore the rendered positions (re-render does not touch an unchanged style value)
			var translate = r => "translate(" + r.x + "px, " + r.y + "px)";
			if (s.kind === "group") {
				var gr = this.group_layout[s.key];
				if (gr) s.el.style.transform = translate(gr);
				(s.followers || []).forEach(f => {
					var fr = this.layout[f.key];
					if (fr) f.el.style.transform = translate(fr);
				});
				this.moving_group = null;
			} else {
				var r = this.layout[s.key];
				if (r) s.el.style.transform = translate(r);
				this.moving_key = null;
			}
			this.landing_bottom = 0;
			this.stop_animation();
		},
		is_compact () {
			return !!(this.$compact_view && this.$compact_view());
		},
		item_style (item) {
			var key = this.item_key(item);
			var width = this.sizes[key];
			var style = {};
			if (width && this.can_resize) style["--ks-video-grid-item-width"] = width + "px";
			if (this.free_layout) {
				var s = this.move;
				var r = (s && s.moved && s.kind === "item" && s.key === key) ? s : this.layout[key];
				if (r) style.transform = "translate(" + r.x + "px, " + r.y + "px)";
				// Videos are narrowed to fit inside their group
				var l = this.full_layout.local[key];
				if (l) style["--ks-video-grid-item-width"] = l.w + "px";
				if (item.free_size && this.free_heights[key]) style.height = this.free_heights[key] + "px";
			}
			return style;
		},
		start_drag (e, item) {
			if (e.button !== 0 || this.move) return;
			// Close tooltip (and cancel its pending show) when resizing starts
			if (e.target.__vueTooltipLeave__) e.target.__vueTooltipLeave__();
			var el = e.target.parentElement;
			e.target.setPointerCapture(e.pointerId);
			this.commit_layout();
			this.start_animation();
			var rect = el.getBoundingClientRect();
			this.drag_state = {
				key: this.item_key(item),
				pointer_id: e.pointerId,
				start_x: e.clientX,
				start_y: e.clientY,
				start_width: rect.width,
				start_height: rect.height,
				// Free size items are resized in both directions
				free: !!item.free_size && this.free_layout,
				width: null,
				height: null,
				shift: false
			};
		},
		drag (e, item) {
			var s = this.drag_state;
			if (!s || s.key !== this.item_key(item) || s.pointer_id !== e.pointerId) return;
			// Button released without pointerup reaching us (e.g. trackpad): stop dragging
			if (e.buttons === 0) {
				this.end_drag(e);
				return;
			}
			this.resize_event = {client_x: e.clientX, client_y: e.clientY, shift: e.shiftKey};
			if (this.resize_frame === null) this.resize_frame = requestAnimationFrame(this.apply_resize);
		},
		// Applied at most once per frame; every size change re-renders and re-lays out the grid
		apply_resize () {
			this.resize_frame = null;
			var s = this.drag_state;
			var e = this.resize_event;
			if (!s || !e) return;
			var width = Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, s.start_width + e.client_x - s.start_x)));
			if (Math.abs(width - DEFAULT_WIDTH) <= SNAP_DISTANCE) width = DEFAULT_WIDTH;
			var height = s.free ? Math.round(Math.min(MAX_FREE_HEIGHT, Math.max(MIN_FREE_HEIGHT, s.start_height + e.client_y - s.start_y))) : null;
			if (width === s.width && height === s.height && e.shift === s.shift) return;
			s.width = width;
			s.height = height;
			s.shift = e.shift;
			if (height !== null) this.$set(this.free_heights, s.key, height);
			if (e.shift) {
				this.items.forEach(i => this.$set(this.sizes, this.item_key(i), width));
			} else {
				this.$set(this.sizes, s.key, width);
			}
		},
		end_drag (e) {
			if (!this.drag_state) return;
			if (this.resize_frame !== null) {
				cancelAnimationFrame(this.resize_frame);
				this.resize_frame = null;
				this.apply_resize();
			}
			// Keep the resized item first while committing so the others stay out of its way
			this.commit_layout();
			this.drag_state = null;
			this.stop_animation();
			this.save_layout();
			if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
				e.target.releasePointerCapture(e.pointerId);
			}
		},
		// "Previous" is the item before this one in reading order (top to bottom, left to right)
		previous_item_width (item) {
			var key = this.item_key(item);
			if (this.free_layout) {
				var layout = this.layout;
				var keys = Object.keys(layout).sort((a, b) => (layout[a].y - layout[b].y) || (layout[a].x - layout[b].x));
				var idx = keys.indexOf(String(key));
				return idx > 0 ? layout[keys[idx - 1]].w : null;
			}
			var list = this.display_items;
			var i = list.findIndex(it => this.item_key(it) === key);
			if (i <= 0) return null;
			var prev_key = this.item_key(list[i - 1]);
			return this.sizes[prev_key] || DEFAULT_WIDTH;
		},
		match_previous_size (e, item) {
			var width = this.previous_item_width(item);
			if (!width) {
				this.reset_size(item);
				return;
			}
			width = Math.round(width);
			this.$set(this.sizes, this.item_key(item), width);
			this.$nextTick(() => {
				this.commit_layout();
				this.save_layout();
			});
		},
		reset_size (item) {
			this.$delete(this.sizes, this.item_key(item));
			this.$delete(this.free_heights, this.item_key(item));
			this.$nextTick(() => {
				this.commit_layout();
				this.save_layout();
			});
		},
		// dir is one of GROUP_HANDLES: the edges/corner being dragged
		start_group_resize (e, group, dir) {
			if (e.button !== 0 || this.move || this.drag_state || this.group_drag) return;
			var r = this.group_layout[group.id];
			if (!r) return;
			e.target.setPointerCapture(e.pointerId);
			this.commit_layout();
			this.start_animation();
			// Moving the left or top edge moves the group, so its videos are shifted the other way to stay in place.
			// The edge can't pass the videos: min_dx/min_dy is the free space before the first video.
			var local = this.full_layout.local;
			var members = {};
			var min_dx = Infinity;
			var min_dy = Infinity;
			Object.keys(local).forEach(k => {
				if (this.members[k] !== group.id) return;
				members[k] = {x: local[k].x, y: local[k].y};
				min_dx = Math.min(min_dx, local[k].x);
				min_dy = Math.min(min_dy, local[k].y);
			});
			this.group_drag = {
				id: group.id,
				dir: dir,
				pointer_id: e.pointerId,
				start_x: e.clientX,
				start_y: e.clientY,
				rect: {x: r.x, y: r.y, w: r.w, h: r.h},
				members: members,
				min_dx: min_dx,
				min_dy: min_dy,
				event: null,
				frame: null
			};
		},
		group_resize_move (e, group) {
			var s = this.group_drag;
			if (!s || s.id !== group.id || s.pointer_id !== e.pointerId) return;
			if (e.buttons === 0) {
				this.end_group_resize(e);
				return;
			}
			s.event = {client_x: e.clientX, client_y: e.clientY};
			if (s.frame === null) s.frame = requestAnimationFrame(this.apply_group_resize);
		},
		// Applied at most once per frame; the group can't get smaller than its videos need
		apply_group_resize () {
			var s = this.group_drag;
			if (!s) return;
			s.frame = null;
			var e = s.event;
			if (!e) return;
			var r = s.rect;
			var dx = e.client_x - s.start_x;
			var dy = e.client_y - s.start_y;
			var x = r.x;
			var y = r.y;
			var w = r.w;
			var h = r.h;
			if (s.dir.includes("e")) {
				w = Math.min(Math.max(MIN_GROUP_WIDTH, this.container_width - r.x), Math.max(MIN_GROUP_WIDTH, r.w + dx));
			}
			if (s.dir.includes("w")) {
				x = Math.min(r.x + s.min_dx, Math.max(0, Math.min(r.x + r.w - MIN_GROUP_WIDTH, r.x + dx)));
				w = r.x + r.w - x;
			}
			if (s.dir.includes("s")) {
				h = Math.max(MIN_GROUP_HEIGHT, r.h + dy);
			}
			if (s.dir.includes("n")) {
				y = Math.min(r.y + s.min_dy, Math.max(0, Math.min(r.y + r.h - MIN_GROUP_HEIGHT, r.y + dy)));
				h = r.y + r.h - y;
			}
			x = Math.round(x);
			y = Math.round(y);
			w = Math.round(w);
			h = Math.round(h);
			var g = this.group_rects[s.id] || {};
			if (g.x === x && g.y === y && g.w === w && g.h === h) return;
			this.$set(this.group_rects, s.id, {x: x, y: y, w: w, h: h});
			Object.keys(s.members).forEach(k => {
				var m = s.members[k];
				this.$set(this.positions, k, {x: m.x - (x - r.x), y: m.y - (y - r.y)});
			});
		},
		end_group_resize (e) {
			var s = this.group_drag;
			if (!s || s.pointer_id !== e.pointerId) return;
			if (s.frame !== null) {
				cancelAnimationFrame(s.frame);
				s.frame = null;
			}
			this.apply_group_resize();
			// Keep the resized group first while committing so the others stay out of its way
			this.commit_layout();
			this.group_drag = null;
			this.stop_animation();
			this.save_layout();
			if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
				e.target.releasePointerCapture(e.pointerId);
			}
		},
		start_edit_title (group) {
			this.editing_group = group.id;
			this.editing_title = group.title || "";
			this.$nextTick(() => {
				var input = [].concat(this.$refs.title_input || [])[0];
				if (input) {
					input.focus();
					input.select();
				}
			});
		},
		finish_edit_title () {
			var id = this.editing_group;
			if (id === null) return;
			this.editing_group = null;
			var group = this.groups.find(g => g.id === id);
			var title = this.editing_title.trim();
			if (group && title && title !== group.title) this.$emit("group-title", {id: id, title: title});
		},
		cancel_edit_title () {
			this.editing_group = null;
		},
		// Forgets the saved position and group of the keys, so they are placed like new items:
		// in the first free spot from the top left, row by row
		place_as_new (keys) {
			keys.forEach(k => {
				this.$delete(this.positions, k);
				this.$delete(this.members, k);
			});
			this.$nextTick(() => {
				this.commit_layout();
				this.save_layout();
			});
		},
		// Drops everything saved for items not in `keys` and for groups no longer in the groups prop,
		// so the saved layout doesn't keep growing. `keys` must contain every item still in use,
		// also the ones not shown at the moment.
		prune_layout (keys) {
			var keep = {};
			keys.forEach(k => { keep[k] = true; });
			var ids = {};
			this.groups.forEach(g => { ids[g.id] = true; });
			var changed = false;
			Object.keys(this.group_rects).forEach(id => {
				if (ids[id]) return;
				this.$delete(this.group_rects, id);
				changed = true;
			});
			Object.keys(this.members).forEach(k => {
				if (keep[k] && ids[this.members[k]]) return;
				// A position relative to a removed group is meaningless
				this.$delete(this.members, k);
				this.$delete(this.positions, k);
				changed = true;
			});
			[this.sizes, this.free_heights, this.positions].forEach(map => {
				Object.keys(map).forEach(k => {
					if (keep[k]) return;
					this.$delete(map, k);
					changed = true;
				});
			});
			Object.keys(this.heights).forEach(k => {
				if (!keep[k]) this.$delete(this.heights, k);
			});
			if (changed) {
				this.$nextTick(() => {
					this.commit_layout();
					this.save_layout();
				});
			}
		},
		// Sizes the group tightly around its videos; empty space on the left and top is removed
		// by moving the group, so the videos stay where they are on the screen
		fit_group (group) {
			var r = this.group_layout[group.id];
			if (!r) return;
			var fl = this.full_layout;
			var keys = Object.keys(fl.local).filter(k => this.members[k] === group.id);
			var min_x = 0;
			var min_y = 0;
			var right = 0;
			var bottom = 0;
			if (keys.length) {
				min_x = Math.min.apply(null, keys.map(k => fl.local[k].x));
				min_y = Math.min.apply(null, keys.map(k => fl.local[k].y));
				right = Math.max.apply(null, keys.map(k => fl.local[k].x + fl.local[k].w)) - min_x;
				bottom = Math.max.apply(null, keys.map(k => fl.local[k].y + fl.local[k].h)) - min_y;
			}
			this.commit_layout();
			keys.forEach(k => {
				var l = fl.local[k];
				this.$set(this.positions, k, {x: l.x - min_x, y: l.y - min_y});
			});
			this.$set(this.group_rects, group.id, {
				x: r.x + min_x,
				y: r.y + min_y,
				w: Math.max(MIN_GROUP_WIDTH, 2 * GROUP_PADDING + right),
				h: Math.max(MIN_GROUP_HEIGHT, GROUP_HEADER + bottom + GROUP_PADDING)
			});
			this.start_animation();
			this.$nextTick(() => {
				this.commit_layout();
				this.save_layout();
				this.stop_animation();
			});
		},
		// Keys of the shown items inside the group
		group_member_keys (group) {
			return this.items.map(i => this.item_key(i)).filter(k => this.members[k] === group.id);
		},
		// Emits the group id and the keys of the shown items inside it
		remove_group (group) {
			this.$emit("group-remove", group.id, this.group_member_keys(group));
		},
		item_key (item) {
			return item.id || item.uuid;
		}
	}
}
</script>
<style scoped>
.ks-video-grid-container {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 5px; /* Väli videoiden väliin */
}
.ks-video-grid-item {
	position: relative;
	isolation: isolate;
	box-sizing: border-box;
	width: var(--ks-video-grid-item-width, 300px);
	flex: 0 0 auto;
}
.ks-video-grid-free {
	display: block;
	position: relative;
}
.ks-video-grid-free > .ks-video-grid-item {
	position: absolute;
	left: 0;
	top: 0;
	max-width: 100%;
}
.ks-video-grid-animate > .ks-video-grid-item {
	transition: transform 0.12s ease-out;
}
.ks-video-grid-animate > .ks-video-grid-item-dragging {
	transition: none;
}
.ks-video-grid-item-dragging {
	z-index: 2;
	will-change: transform;
	opacity: 0.8;
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
}
.ks-video-grid-placeholder {
	position: absolute;
	left: 0;
	top: 0;
	box-sizing: border-box;
	border: 2px dashed var(--ks-col-primary, #4a90d9);
	border-radius: 4px;
	pointer-events: none;
}
.ks-video-grid-moving {
	user-select: none;
}
.ks-video-area-group {
	position: absolute;
	left: 0;
	top: 0;
	box-sizing: border-box;
	border: 1px solid var(--ks-col-secondary);
	border-radius: 6px;
	background: var(--ks-video-area-group-background, var(--ks-background-color-light-op60));
}
.ks-video-grid-animate > .ks-video-area-group {
	transition: transform 0.12s ease-out;
}
.ks-video-grid-animate > .ks-video-area-group-dragging,
.ks-video-grid-animate > .ks-video-area-group-resizing {
	transition: none;
}
/* Invisible edge and corner handles */
.ks-video-area-group-handle {
	position: absolute;
	z-index: 2;
	touch-action: none;
}
.ks-video-area-group-handle-n,
.ks-video-area-group-handle-s {
	left: 8px;
	right: 8px;
	height: 6px;
	cursor: ns-resize;
}
.ks-video-area-group-handle-n {
	top: -3px;
}
.ks-video-area-group-handle-s {
	bottom: -3px;
}
.ks-video-area-group-handle-e,
.ks-video-area-group-handle-w {
	top: 8px;
	bottom: 8px;
	width: 6px;
	cursor: ew-resize;
}
.ks-video-area-group-handle-e {
	right: -3px;
}
.ks-video-area-group-handle-w {
	left: -3px;
}
.ks-video-area-group-handle-ne,
.ks-video-area-group-handle-nw,
.ks-video-area-group-handle-se,
.ks-video-area-group-handle-sw {
	width: 10px;
	height: 10px;
}
.ks-video-area-group-handle-se {
	bottom: -3px;
	right: -3px;
	cursor: nwse-resize;
}
.ks-video-area-group-handle-ne {
	top: -3px;
	right: -3px;
	cursor: nesw-resize;
}
.ks-video-area-group-handle-nw {
	top: -3px;
	left: -3px;
	cursor: nwse-resize;
}
.ks-video-area-group-handle-sw {
	bottom: -3px;
	left: -3px;
	cursor: nesw-resize;
}
.ks-video-area-group-dragging {
	will-change: transform;
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
}
.ks-video-area-group-drop {
	border-color: var(--ks-col-accent);
}
.ks-video-area-group-heading {
	display: flex;
	align-items: center;
	gap: 4px;
	box-sizing: border-box;
	height: 30px;
	padding: 0 4px 0 8px;
	cursor: grab;
	user-select: none;
	touch-action: none;
	font-size: var(--ks-font-size-s);
}
.ks-video-area-group-dragging .ks-video-area-group-heading {
	cursor: grabbing;
}
.ks-video-area-group-heading .ks-btn {
	cursor: pointer;
}
.ks-video-area-group-title {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.ks-video-area-group-title-input {
	flex: 1 1 auto;
	min-width: 0;
	box-sizing: border-box;
	padding: 2px 4px;
	border: 1px solid var(--ks-col-accent);
	border-radius: 3px;
	background: var(--ks-background-color-dark);
	color: inherit;
	font: inherit;
	outline: none;
	cursor: text;
}
.ks-video-grid-resize-handle {
	position: absolute;
	right: 0;
	bottom: 0;
	width: 16px;
	height: 16px;
	cursor: nwse-resize;
	touch-action: none;
	z-index: 1;
	opacity: 0.5;
	background: linear-gradient(135deg, transparent 50%, var(--ks-col-secondary, #888) 50%, var(--ks-col-secondary, #888) 60%, transparent 60%, transparent 75%, var(--ks-col-secondary, #888) 75%, var(--ks-col-secondary, #888) 85%, transparent 85%);
}
.ks-video-grid-resize-handle:hover {
	opacity: 1;
}
@media (max-width: 768px) {
	.ks-video-grid-resize-handle {
		display: none;
	}
	.ks-video-grid-item {
		width: 100%;
	}
}
</style>
<style>
/* Not scoped: the heading belongs to a child component */
.ks-video-grid-item-reorderable .ks-panel-heading {
	cursor: grab;
	user-select: none;
	touch-action: none;
}
.ks-video-grid-item-dragging .ks-panel-heading,
.ks-video-grid-moving {
	cursor: grabbing;
}
/* Same as sidebar resizing: players must not react to the pointer passing over them */
.ks-video-grid-moving video,
.ks-video-grid-moving iframe {
	pointer-events: none;
}
.ks-video-grid-item-reorderable .ks-panel-heading button,
.ks-video-grid-item-reorderable .ks-panel-heading a,
.ks-video-grid-item-reorderable .ks-panel-heading .ks-btn {
	cursor: pointer;
}
</style>